import React, { useState } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

import { useForcedNav } from "./hooks/use-forced-nav";
import {
    GlobalNavLinkContentProps,
    useGlobalNavContext
} from "./nav-links-container";

export type GlobalNavLinkProps = GlobalNavLinkContentProps & {
    isLinkVisible?: boolean;
};

const buildAnchorLinkClassName = (
    navLinkIsCurrentPage: boolean,
    isCurrentOpenDropdown: boolean,
    hasSubnav: boolean,
    isActive?: boolean
): string => {
    return (
        `glob-nav-anchor-link nav-link-anchor` +
        `${navLinkIsCurrentPage ? " isCurrentPage " : ""}` +
        `${!!isCurrentOpenDropdown && !!hasSubnav ? "isActive" : ""}` +
        `${!hasSubnav ? "isHoverableLink" : ""}` +
        `${isActive ? " isActive " : ""}`
    );
};

const GlobalNavLink = (props: GlobalNavLinkProps) => {
    // Using useGlobalNavContext hook which uses context, so component must memoize
    // to prevent unnecessary re-renders when context changes but props don't
    const { current, setCurrent } = useGlobalNavContext();
    const { isForcingNav, forcedNavKey } = useForcedNav();

    const isCurrentOpenDropdown = current === props.globalLinksKey;
    const navLinkIsCurrentPage =
        props.href && globalThis.location.pathname.includes(props.href);
    const [isOpen, setIsOpen] = useState(false);

    const anchorLinkClassName = buildAnchorLinkClassName(
        !!navLinkIsCurrentPage,
        isCurrentOpenDropdown,
        !!props.hasSubnav,
        !!props.isActive
    );

    const linkClickAction: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
        if (props.hasSubnav) {
            e.preventDefault();

            if (isForcingNav && forcedNavKey !== props.globalLinksKey) {
                return;
            }

            if (isOpen) {
                setCurrent("");
                setIsOpen(false);
            } else {
                setCurrent(props.globalLinksKey);
                setIsOpen(true);
            }
        }
    };

    const rotate180deg = css({
        transform: "rotate(180deg)"
    });

    return (
        <a
            className={anchorLinkClassName}
            style={{
                pointerEvents: props.isLinkVisible === false ? "none" : "auto"
            }}
            css={css({
                paddingBlockStart: token("space.100"),
                display: "flex",
                alignItems: "center",
                height: "100%",
                textDecoration: "none",
                lineHeight: "1rem",

                // if padding val needs to change, update calculateMaxChildren function (search for jfind-1024)
                paddingInlineEnd: token("space.400"),
                ["&.isActive"]: {
                    color: wacToken("color.extended.blue800")
                },
                ["&:hover"]: {
                    color: wacToken("color.primary.blue")
                },
                [media.above.md]: {
                    borderRadius: "3px",
                    textDecoration: "none"
                }
            })}
            href={props.href ?? "#"}
            onClick={linkClickAction}
            onMouseEnter={() => {
                if (isForcingNav && forcedNavKey !== props.globalLinksKey) {
                    return;
                }

                setCurrent(props.globalLinksKey);
                setIsOpen(true);
            }}
            onFocus={() => {
                if (isForcingNav && forcedNavKey !== props.globalLinksKey) {
                    return;
                }

                setCurrent(props.globalLinksKey);
                setIsOpen(true);
            }}
            aria-label={props.linkText}
            aria-expanded={
                props.hasSubnav ? !!isCurrentOpenDropdown : undefined
            }
            aria-current={navLinkIsCurrentPage ? "page" : undefined}
            role={props.hasSubnav ? "button" : undefined}
            data-event="clicked"
            data-event-component="linkButton"
            data-event-container="headerDropdownLink"
            data-product-key={props.productKey ?? "jiraSoftware"}
        >
            {props.linkText}
            {!!props.hasSubnav && (
                <div
                    className="chevron-icon"
                    style={{
                        opacity: props.isLinkVisible === false ? 0 : 1
                    }}
                    css={[
                        css({
                            display: "block",
                            marginInlineStart: "8px",
                            lineHeight: 0,
                            transition: "250ms"
                        }),
                        isCurrentOpenDropdown && rotate180deg
                    ]}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="11"
                        height="6"
                        viewBox="0 0 11 6"
                        fill="none"
                    >
                        <path
                            d="M9.19922 1.18176L5.19922 4.81813L1.19922 1.18176"
                            stroke={
                                isCurrentOpenDropdown
                                    ? wacToken("color.extended.blue800")
                                    : wacToken("color.primary.black")
                            }
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            )}
        </a>
    );
};

// Memoize to prevent unnecessary re-renders when parent component updates
// but props haven't changed. This is important because GlobalNavLink consumes
// GlobalNavContext which updates frequently (when dropdowns open/close),
// and we only want to re-render this link if its props actually change.
export default React.memo(GlobalNavLink);
